import {
	LengthValue,
	NotificationRoot,
	respondTo,
	toMsValue,
} from "@animech/ui";
import { css, CSSObject } from "@emotion/react";
import styled from "@emotion/styled";
import { ZIndex } from "src/ui/constants";

export const Notifications = styled(NotificationRoot)``;

/*
	Panel dimensions [expanded, collapsed/minimized]
*/
export const leftPanelWidth: [LengthValue, LengthValue] = ["300px", "0px"];
export const rightPanelWidth: [LengthValue, LengthValue] = ["250px", "0px"];

/* Clears the cart button and price block that share the right panel's column. */
export const rightPanelTopOffset: LengthValue = "120px";
export const topPanelHeight: [LengthValue, LengthValue] = ["0px", "0px"];
export const bottomPanelHeight: [LengthValue, LengthValue] = ["350px", "0px"];

export type PanelPosition = "top" | "bottom" | "left" | "right";
export type PanelLayout = "column" | "row";
type LayoutPanelProps = {
	side: PanelPosition;
	open?: boolean;
};

export const LayoutContainer = styled.div`
	${({ children, theme }) => {
		const offsets: Record<
			PanelPosition,
			{ current: LengthValue; min: LengthValue; max: LengthValue }
		> = {
			left: {
				current: leftPanelWidth[1],
				min: leftPanelWidth[1],
				max: leftPanelWidth[0],
			},
			right: {
				current: rightPanelWidth[1],
				min: rightPanelWidth[1],
				max: rightPanelWidth[0],
			},
			top: {
				current: topPanelHeight[1],
				min: topPanelHeight[1],
				max: topPanelHeight[0],
			},
			bottom: {
				current: bottomPanelHeight[1],
				min: bottomPanelHeight[1],
				max: bottomPanelHeight[0],
			},
		};

		/*
			Loop through the Layout children, check for open panels and adjust offsets accordingly
		*/
		if (Array.isArray(children) && children.length) {
			for (const child of children) {
				if (typeof child === "object" && child && "props" in child) {
					const props = (child as React.ReactElement<LayoutPanelProps>).props;
					if (props.side && props.open) {
						offsets[props.side].current = offsets[props.side].max;
					}
				}
			}
		}

		return css`
			--left-panel-offset: ${offsets.left.current};
			--right-panel-offset: ${offsets.right.current};
			--top-panel-offset: ${offsets.top.current};
			--bottom-panel-offset: ${offsets.bottom.current};

			${respondTo(theme, "mobile")} {
				--left-panel-offset: 0px;
				--bottom-panel-offset: 0px;
			}

			position: relative;
			height: 100%;
			width: 100vw;
			user-select: none;
			pointer-events: none;

			${LayoutPanel} {
				> button {
					font-weight: 300;
					line-height: 30px;
				}
			}
		`;
	}}
`;

export const LayoutCanvas = styled.div`
	${({ theme }) => {
		const horizontalMax = `calc(${leftPanelWidth[0]} + ${rightPanelWidth[0]})`;
		const verticalMax = `calc(${topPanelHeight[0]} + ${bottomPanelHeight[0]})`;
		const horizontalAdjustment = `0px`;
		const verticalAdjustment = `0px`;
		const horizontalDynicAdjustment = `(var(--left-panel-offset) / 2 * -1 + var(--right-panel-offset) / 2)`;
		const verticalDynicAdjustment = `(var(--top-panel-offset) / 2 * -1 + var(--bottom-panel-offset) / 2)`;

		return css`
			position: absolute;
			width: calc(100vw + ${horizontalMax});
			height: calc(100vh + ${verticalMax});
			left: calc(50% + ${horizontalAdjustment});
			top: calc(50% + ${verticalAdjustment});
			pointer-events: auto;
			user-select: auto;
			transform-style: preserve-3d;
			backface-visibility: hidden;
			will-change: transform;
			transition: transform ${toMsValue(theme.app.panels.transitionDuration)}
				${theme.app.panels.transitionTimingFunction};
			transform: translate3d(
				calc(-50% - ${horizontalDynicAdjustment}),
				calc(-50% - ${verticalDynicAdjustment}),
				0
			);

			${respondTo(theme, "mobile")} {
				top: 0;
				left: 0;
				width: 100vw;
				height: 100vh;
				height: 100dvh;
				transform: translateY(0);
				transition: transform ${theme.app.mobile.transitionDuration}ms
					${theme.app.mobile.transitionTimingFunction};

				&[data-panel-open] {
					transform: translateY(calc(var(--mobile-panel-height, 60vh) / -2));
				}
			}
		`;
	}}
`;

type LayoutViewportProps = {
	dragOperationActive?: boolean;
};
export const LayoutViewport = styled.div<LayoutViewportProps>`
	${({ theme, dragOperationActive = false }) => css`
		position: absolute;
		left: var(--left-panel-offset);
		right: var(--right-panel-offset);
		top: var(--top-panel-offset);
		bottom: var(--bottom-panel-offset);
		display: grid;
		user-select: none;
		pointer-events: none;
		will-change: left, right, top, bottom;
		transform-style: preserve-3d;
		backface-visibility: hidden;
		transition-property: left, right, top, bottom;
		transition-duration: ${toMsValue(theme.app.panels.transitionDuration)};
		transition-timing-function: ${theme.app.panels.transitionTimingFunction};
		transition-delay: 0;
		z-index: ${ZIndex.LayoutViewport};

		/* Disable pointer events on all the descendants of the LayoutViewport during drag event to avoid 
		* "dropping" the dragged object when intersecting a viewport element. */
		${dragOperationActive
			? css`
					&,
					& * {
						pointer-events: none !important;
					}
				`
			: css`
					> * {
						pointer-events: auto;
					}
				`}
	`}
`;

export const LayoutPanel = styled.div<LayoutPanelProps>`
	${({ theme, side, open = false }) => {
		const style: CSSObject = {};

		if (side === "left" || side === "right") {
			style.top = 0;
			style.bottom = 0;
			style[side] = 0;
			style.flexDirection = "column";

			if (side === "left") {
				const shouldHide = parseInt(leftPanelWidth[1], 10) === 0;
				style.width = `calc(${open ? leftPanelWidth[0] : shouldHide ? leftPanelWidth[0] : leftPanelWidth[1]})`;
				style.margin = 0;
				style.left =
					open || !shouldHide ? 0 : `calc(${leftPanelWidth[0]} * -1)`;
				style.willChange = "width, left";
				style.transitionProperty = "width, left";
			} else if (side === "right") {
				const shouldHide = parseInt(rightPanelWidth[1], 10) === 0;
				style.width = `calc(${open ? rightPanelWidth[0] : shouldHide ? rightPanelWidth[0] : rightPanelWidth[1]} - ${theme.app.viewport.padding})`;
				style.top = rightPanelTopOffset;
				style.marginLeft = 0;
				style.right =
					open || !shouldHide ? 0 : `calc(${rightPanelWidth[0]} * -1)`;
				style.willChange = "width, right";
				style.transitionProperty = "width, right";
			}
		} else {
			style.left = "var(--left-panel-offset)";
			style.right = "var(--right-panel-offset)";
			style.flexDirection = "row";

			if (side === "top") {
				const shouldHide = parseInt(topPanelHeight[1], 10) === 0;
				style.height = `calc(${open ? topPanelHeight[0] : shouldHide ? topPanelHeight[0] : topPanelHeight[1]} - ${theme.app.viewport.padding})`;
				style.marginBottom = 0;
				style.top = open || !shouldHide ? 0 : `calc(${topPanelHeight[0]} * -1)`;
				style.willChange = "height, left, right, top";
				style.transitionProperty = "height, left, right, top";
			} else if (side === "bottom") {
				const shouldHide = parseInt(bottomPanelHeight[1], 10) === 0;
				style.height = `calc(${open ? bottomPanelHeight[0] : shouldHide ? bottomPanelHeight[0] : bottomPanelHeight[1]} - ${theme.app.viewport.padding})`;
				style.marginTop = 0;
				style.bottom =
					open || !shouldHide ? 0 : `calc(${bottomPanelHeight[0]} * -1)`;
				style.willChange = "height, left, right, bottom";
				style.transitionProperty = "height, left, right, bottom";
			}
		}

		return css`
			position: absolute;
			margin: ${theme.app.viewport.padding};
			display: flex;
			align-items: center;
			justify-content: center;
			user-select: none;
			pointer-events: none;
			transform-style: preserve-3d;
			backface-visibility: hidden;
			transition-duration: ${toMsValue(theme.app.panels.transitionDuration)};
			transition-timing-function: ${theme.app.panels.transitionTimingFunction};
			overflow: hidden;
			align-items: stretch;
			gap: ${theme.app.viewport.padding};
			z-index: ${ZIndex.LayoutPanel};

			${style}

			${side === "bottom"
				? css`
						${respondTo(theme, "mobile")} {
							position: fixed;
							bottom: 0;
							left: 0;
							right: 0;
							top: unset;
							height: var(--mobile-panel-height, 60vh);
							width: 100%;
							margin: 0;
							z-index: ${ZIndex.MobilePanel};
							border-radius: ${theme.app.mobile.panelBorderRadius}
								${theme.app.mobile.panelBorderRadius} 0 0;
							background: ${theme.colors.backgrounds[0].background};
							transition:
								transform ${theme.app.mobile.transitionDuration}ms
									${theme.app.mobile.transitionTimingFunction},
								visibility 0s
									${open ? "0s" : `${theme.app.mobile.transitionDuration}ms`};
							transform: translateY(${open ? "0" : "100%"});
							visibility: ${open ? "visible" : "hidden"};
							overflow: hidden;
							flex-direction: column;
							padding-bottom: env(safe-area-inset-bottom, 0px);
						}
					`
				: ""}

			> * {
				pointer-events: auto;
			}
		`;
	}}
`;

export const DragHandle = styled.div`
	display: flex;
	justify-content: center;
	align-items: center;
	padding: 0;
	cursor: grab;
	pointer-events: auto;
	touch-action: none;
	flex-shrink: 0;
	min-height: ${({ theme }) => theme.app.mobile.minTouchTarget};

	&:active {
		cursor: grabbing;
	}

	&::before {
		content: "";
		width: 40px;
		height: 4px;
		border-radius: 2px;
		background: ${({ theme }) => theme.app.shadows.dragHandle};
	}
`;

export const MobilePanelContent = styled.div`
	overflow-x: clip;
	overflow-y: auto;
	touch-action: pan-y;
	flex: 1;
	min-height: 0;
	-webkit-overflow-scrolling: touch;
	pointer-events: auto;
`;

type MultiEditPenContainerProps = {
	leftSidebarOpen: boolean;
};
export const MultiEditPenContainer = styled.div<MultiEditPenContainerProps>`
	${({ theme, leftSidebarOpen }) => {
		const width = leftPanelWidth[0];
		const left = leftSidebarOpen ? width : 0;
		return css`
			position: absolute;
			left: ${left};
			top: 0px;
			height: 100%;
			width: ${width};
			background-color: ${theme.colors.backgrounds[0].background};
			padding: 12px 0;
			z-index: ${ZIndex.LayoutPanel};
			border-left: 1px solid ${theme.colors.backgrounds[0].background};
			box-shadow: 3px 0 5px -2px inset ${theme.colors.backgrounds[4].background};
		`;
	}}
`;

export const Root = styled.main`
	display: block;
	height: 100%;
	width: 100%;
	overflow: hidden;

	${Notifications} {
		grid-column: 1 / 1;
		grid-row: 1 / 1;
		position: relative;
		padding-bottom: 70px;
	}
`;
